import { el, svg } from "../core/dom";
import { createRequiredMarker } from "../core/required-marker";
import { productPurposeKey } from "../core/productMatrix";
import { part } from "../appearance/parts";
import { CONFIRM_HINT_ID, PRODUCT_SECTION_COPY } from "../constants";
import { styles as classicStyles } from "../styles/modal";
import type {
  ProductGroupsOptions,
  ProductSectionOptions,
  ProductSectionRefs,
  SelectablePurpose,
} from "../types";
import {
  CLASSIC_PURPOSE_LOOK,
  renderModalPurposes,
  type ModalPurposeRefs,
} from "./purposes";
import { renderSelectionControl } from "./selection-control";
import { productBoxPurposes } from "./productConsent";
import { productIsRequired } from "./productState";

const CHEVRON_OPEN = "rotate(90deg)";
const CHEVRON_CLOSED = "rotate(0deg)";

const sectionTally = (
  boxPurposes: ReadonlyArray<SelectablePurpose>,
  keyProductUuid: string | undefined,
  selectedPurposes: Record<string, boolean>
) => {
  const selectedCount = boxPurposes.filter(
    (purpose) => selectedPurposes[productPurposeKey(keyProductUuid, purpose.uuid)]
  ).length;
  return {
    allSelected: boxPurposes.length > 0 && selectedCount === boxPurposes.length,
    someSelected: selectedCount > 0,
  };
};

/**
 * A partly ticked section's middle state. A checkbox shows its dash and the
 * two-option controls hold neither answer; a switch has two positions, so it
 * shows off and the click that follows asks for all of it.
 */
const showsMidState = (
  tally: { allSelected: boolean; someSelected: boolean },
  drawsSwitch: boolean
): boolean => !drawsSwitch && tally.someSelected && !tally.allSelected;

export function renderProductSection(options: ProductSectionOptions): ProductSectionRefs {
  const {
    product,
    purposes,
    keyProductUuid,
    selectedPurposes,
    collapsed,
    locked,
    isRequired,
    getTranslatedText,
    onToggleCollapse,
    onToggleSelect,
  } = options;
  const look = options.look ?? CLASSIC_PURPOSE_LOOK;
  const styles = look.styles;

  const name = getTranslatedText("products.name", product.name, product.uuid);
  const headingColor = look.headingColor;
  const boxPurposes = productBoxPurposes(purposes);
  // Switches only stand in for checkboxes; a radio or dropdown header draws no
  // switch, so it keeps its own middle state.
  const drawsSwitch = look.drawsSwitches && look.selectionControl === "checkbox";

  const chevron = svg(
    "svg",
    {
      width: "7",
      height: "12",
      viewBox: "0 0 7 12",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      stroke: headingColor,
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      style: {
        transform: collapsed ? CHEVRON_CLOSED : CHEVRON_OPEN,
        transition: "transform 0.3s ease",
      },
      "aria-hidden": "true",
    },
    svg("path", { d: "M1 1L6 6L1 11" })
  );

  // The button is the chevron alone, not the heading's container: ARIA gives
  // role=button presentational children, so an <h2> inside one is stripped
  // from the heading tree. aria-labelledby names it instead.
  const collapseButton = el(
    "button",
    {
      type: "button",
      style: classicStyles.chevronButton,
      "aria-expanded": String(!collapsed),
      "aria-controls": `product-${product.uuid}`,
      "aria-labelledby": `product-name-${product.uuid}`,
      onClick: () => onToggleCollapse(product.uuid),
    },
    el(
      "div",
      { style: { display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "5px" } },
      chevron
    )
  );

  const title = el("h2", {
    id: `product-name-${product.uuid}`,
    style: {
      ...styles.sectionTitle,
      ...look.responsive.optionTitle,
      color: headingColor,
    },
  });
  title.textContent = name;
  // Follows the gate, not the notice: only a product the confirm button
  // actually enforces is marked.
  if (isRequired) {
    title.appendChild(createRequiredMarker());
  }

  const tally = sectionTally(boxPurposes, keyProductUuid, selectedPurposes);
  const selection = renderSelectionControl({
    control: look.selectionControl,
    checked: tally.allSelected,
    indeterminate: showsMidState(tally, drawsSwitch),
    onSelect: (next) => onToggleSelect(product.uuid, next),
    label:
      boxPurposes.length < purposes.length
        ? PRODUCT_SECTION_COPY.toggleRequired(name, boxPurposes.length)
        : PRODUCT_SECTION_COPY.toggleSelection(name, boxPurposes.length),
    groupName: `redacto-product-${product.uuid}`,
    size: "large",
    locked,
    lockedLabel: PRODUCT_SECTION_COPY.settled(name),
    language: look.language,
    skin: look.controlSkin,
  });

  // The heading beside the chevron opens the section too, across the whole
  // row up to the control: a short name such as "Default" otherwise leaves
  // most of the row answering no click. Not a second button, which would
  // strip the <h2> from the heading tree.
  const heading = el(
    "div",
    {
      style: { ...styles.optionTextContainer, cursor: "pointer" },
      onClick: () => onToggleCollapse(product.uuid),
    },
    title
  );

  const header = el(
    "div",
    { ...part("product"), style: styles.sectionHeader },
    el("div", { style: styles.optionLeft }, collapseButton, heading),
    selection.element
  );

  const body = el("div", { id: `product-${product.uuid}`, style: styles.sectionBody });
  body.hidden = collapsed;

  return {
    productUuid: product.uuid,
    keyProductUuid,
    boxPurposes,
    drawsSwitch,
    container: el("div", { className: look.rowClassName ?? null }, header, body),
    body,
    collapseButton,
    chevron,
    control: locked ? null : selection,
  };
}

export function renderProductGroups(options: ProductGroupsOptions): {
  container: HTMLElement;
  purposeRefs: ModalPurposeRefs;
  sections: ProductSectionRefs[];
} {
  const look = options.look ?? CLASSIC_PURPOSE_LOOK;
  const container = options.host ?? el("div", { style: look.styles.optionsContainer });
  const sections: ProductSectionRefs[] = [];
  const parts: ModalPurposeRefs[] = [];

  for (const group of options.groups) {
    const productUuid = group.product.uuid;
    const section = renderProductSection({
      product: group.product,
      purposes: group.purposes,
      keyProductUuid: productUuid,
      selectedPurposes: options.selectedPurposes,
      collapsed: options.collapsedProducts[productUuid] || false,
      locked: options.consentedProducts[productUuid] ?? false,
      isRequired: productIsRequired(options.products, productUuid),
      look,
      getTranslatedText: options.getTranslatedText,
      onToggleCollapse: options.onToggleCollapse,
      onToggleSelect: options.onToggleSelect,
    });
    const rows = renderModalPurposes(group.purposes, {
      selectedPurposes: options.selectedPurposes,
      collapsedPurposes: options.collapsedPurposes,
      selectedDataElements: options.selectedDataElements,
      initialDataElementSelections: options.initialDataElementSelections,
      recordedAnswers: options.recordedAnswers,
      look,
      isAlreadyConsented: false,
      productUuid,
      host: section.body,
      isRowLocked: (purposeUuid) =>
        options.consentedPurposes[productPurposeKey(productUuid, purposeUuid)] ?? false,
      getTranslatedText: options.getTranslatedText,
      onPurposeToggle: options.onPurposeToggle,
      onPurposeCollapse: options.onPurposeCollapse,
      onDataElementToggle: options.onDataElementToggle,
    });
    container.appendChild(section.container);
    sections.push(section);
    parts.push(rows);
  }

  return { container, purposeRefs: mergePurposeRefs(container, parts), sections };
}

export function updateProductSection(
  refs: ProductSectionRefs,
  selectedPurposes: Record<string, boolean>,
  collapsed: boolean
): void {
  if (refs.control) {
    const tally = sectionTally(refs.boxPurposes, refs.keyProductUuid, selectedPurposes);
    refs.control.setChecked(tally.allSelected, showsMidState(tally, refs.drawsSwitch ?? false));
  }
  refs.body.hidden = collapsed;
  refs.collapseButton.setAttribute("aria-expanded", String(!collapsed));
  (refs.chevron as unknown as HTMLElement).style.transform = collapsed
    ? CHEVRON_CLOSED
    : CHEVRON_OPEN;
}

export function mergePurposeRefs(
  container: HTMLElement,
  parts: ReadonlyArray<ModalPurposeRefs>
): ModalPurposeRefs {
  const merge = <V>(pick: (refs: ModalPurposeRefs) => Map<string, V>) =>
    new Map<string, V>(parts.flatMap((refs) => [...pick(refs)]));
  return {
    container,
    elementControls: merge((refs) => refs.elementControls),
    purposeControls: merge((refs) => refs.purposeControls),
    collapseButtons: merge((refs) => refs.collapseButtons),
    dataElementContainers: merge((refs) => refs.dataElementContainers),
    chevrons: merge((refs) => refs.chevrons),
    titleElements: merge((refs) => refs.titleElements),
    descriptionElements: merge((refs) => refs.descriptionElements),
    dataElementLabels: merge((refs) => refs.dataElementLabels),
  };
}

export function renderConfirmHint(style?: Record<string, string | number | undefined>): HTMLElement {
  return el("p", { id: CONFIRM_HINT_ID, role: "status", style: style ?? classicStyles.confirmHint });
}

export function updateConfirmHint(
  hintEl: HTMLElement,
  anchor: HTMLElement,
  describedButton: HTMLElement | null,
  text: string | null
): void {
  if (!text) {
    hintEl.remove();
    describedButton?.removeAttribute("aria-describedby");
    return;
  }
  hintEl.textContent = text;
  if (hintEl.nextSibling !== anchor) {
    anchor.parentNode?.insertBefore(hintEl, anchor);
  }
  describedButton?.setAttribute("aria-describedby", CONFIRM_HINT_ID);
}
